import React, { useEffect, useState } from "react";
import { X, Check, Trash2, ListPlus } from "lucide-react";
import clsx from "clsx";
import { toast } from "sonner";

import { roomsApi, crewApi, equipmentApi, tasksApi } from "@/lib/api";

const ROOM_COLOR_TOKENS = ["magenta", "cyan", "violet", "lime", "amber", "coral", "azure", "rose"];
const PRIORITIES = ["normal", "high", "critical"];

/**
 * Editable preview-and-confirm dialog for AI bulk-add actions.
 * - Producer reviews each row, can edit cells inline or drop rows.
 * - One commit click loops the appropriate API per row, then refreshes.
 */
export default function BulkConfirmDialog({ open, action, rooms = [], projectId, onClose, onCommitted }) {
  const [items, setItems] = useState([]);
  const [parent, setParent] = useState(null);
  const [busy, setBusy] = useState(false);

  // Hydrate editable copy when opened
  useEffect(() => {
    if (!open || !action) return;
    if (action.action === "add_task_with_subtasks") {
      setParent({ ...(action.parent || {}) });
      setItems((action.subtasks || []).map((s, i) => ({ ...s, _id: i })));
    } else {
      setParent(null);
      setItems((action.items || []).map((it, i) => ({ ...it, _id: i })));
    }
  }, [open, action]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !busy) onClose(); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose, busy]);

  if (!open || !action) return null;

  const a = action.action;
  const update = (id, patch) => setItems((arr) => arr.map((it) => (it._id === id ? { ...it, ...patch } : it)));
  const remove = (id) => setItems((arr) => arr.filter((it) => it._id !== id));
  const updateParent = (patch) => setParent((p) => ({ ...p, ...patch }));

  const commit = async () => {
    if (busy) return;
    setBusy(true);
    let ok = 0, fail = 0;
    try {
      if (a === "bulk_add_rooms") {
        for (const it of items) {
          try { await roomsApi.create(projectId, sanitize(it)); ok++; }
          catch (e) { fail++; console.warn("room create failed", it, e); }
        }
      } else if (a === "bulk_add_crew") {
        for (const it of items) {
          try { await crewApi.create(projectId, sanitize(it)); ok++; }
          catch (e) { fail++; console.warn("crew create failed", it, e); }
        }
      } else if (a === "bulk_add_equipment") {
        for (const it of items) {
          try { await equipmentApi.create(projectId, sanitize(it)); ok++; }
          catch (e) { fail++; console.warn("equipment create failed", it, e); }
        }
      } else if (a === "bulk_add_tasks") {
        for (const it of items) {
          try { await tasksApi.create(projectId, sanitize(it)); ok++; }
          catch (e) { fail++; console.warn("task create failed", it, e); }
        }
      } else if (a === "add_task_with_subtasks") {
        try {
          const parentTask = await tasksApi.create(projectId, sanitize(parent));
          ok++;
          for (const sub of items) {
            try {
              await tasksApi.create(projectId, sanitize({ ...sub, parent_task_id: parentTask.id }));
              ok++;
            } catch (e) { fail++; console.warn("subtask create failed", sub, e); }
          }
        } catch (e) {
          fail++; console.warn("parent task create failed", parent, e);
        }
      }
      toast.success(`Added ${ok} item${ok === 1 ? "" : "s"}${fail ? ` · ${fail} failed` : ""}.`);
      await onCommitted?.();
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6"
      onClick={busy ? undefined : onClose}
      data-testid="bulk-confirm-overlay"
    >
      <div
        className="w-[760px] max-w-full max-h-[88vh] bg-bg-surface border border-line-strong shadow-[0_24px_64px_rgba(0,0,0,0.8)] animate-slide-up flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="bulk-confirm-dialog"
      >
        <div className="flex items-center justify-between p-3 border-b border-line-strong bg-bg-elevated">
          <div className="flex items-center gap-2">
            <ListPlus className="w-4 h-4 text-amber" />
            <div>
              <div className="mono text-2xs uppercase tracking-[0.2em] text-ink-mute">AI BULK ADD · REVIEW</div>
              <div className="text-sm text-ink mt-0.5">{labelFor(a, items.length, parent)}</div>
            </div>
          </div>
          <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="bulk-confirm-close">
            <X className="w-3 h-3" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-3 space-y-1">
          {a === "add_task_with_subtasks" && parent && (
            <div className="border border-amber bg-amber-glow p-3 mb-3">
              <div className="mono text-2xs uppercase tracking-widest text-amber mb-2">PARENT TASK</div>
              <ParentRow draft={parent} rooms={rooms} onChange={updateParent} />
            </div>
          )}
          {a === "add_task_with_subtasks" && (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute mb-1">
              SUBTASKS · {items.length}
            </div>
          )}

          {items.length === 0 && (
            <div className="mono text-2xs uppercase tracking-widest text-ink-mute p-3">
              — no items —
            </div>
          )}

          {items.map((it) => (
            <ItemRow
              key={it._id}
              kind={a}
              draft={it}
              rooms={rooms}
              onChange={(patch) => update(it._id, patch)}
              onRemove={() => remove(it._id)}
            />
          ))}
        </div>

        <div className="p-3 border-t border-line bg-bg-surface flex items-center justify-between gap-2">
          <div className="mono text-2xs uppercase tracking-widest text-ink-mute">
            {items.length} item{items.length === 1 ? "" : "s"}{parent ? " + 1 parent" : ""} ready
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onClose} disabled={busy} className="btn-ghost" data-testid="bulk-confirm-cancel">
              CANCEL
            </button>
            <button
              onClick={commit}
              disabled={busy || items.length === 0}
              className="btn-primary px-4"
              data-testid="bulk-confirm-commit"
            >
              <Check className="w-3 h-3" />
              {busy ? "Adding…" : `Add ${items.length}${parent ? " + 1" : ""}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function labelFor(action, count, parent) {
  if (action === "bulk_add_rooms") return `Add ${count} room${count === 1 ? "" : "s"}`;
  if (action === "bulk_add_crew") return `Add ${count} crew member${count === 1 ? "" : "s"}`;
  if (action === "bulk_add_equipment") return `Add ${count} equipment item${count === 1 ? "" : "s"}`;
  if (action === "bulk_add_tasks") return `Add ${count} task${count === 1 ? "" : "s"}`;
  if (action === "add_task_with_subtasks") return `${parent?.title || "New task"} + ${count} subtask${count === 1 ? "" : "s"}`;
  return "Confirm";
}

function sanitize(item) {
  const o = { ...item };
  delete o._id;
  for (const k of Object.keys(o)) if (o[k] === "" || o[k] === undefined) delete o[k];
  return o;
}

// ─── Row renderers ────────────────────────────────────────────
function ItemRow({ kind, draft, rooms, onChange, onRemove }) {
  return (
    <div className="grid items-center gap-2 p-2 border border-line bg-bg-elevated hover:border-amber/60 transition-colors"
         style={{ gridTemplateColumns: gridFor(kind) }}
         data-testid={`bulk-row-${draft._id}`}
    >
      {kind === "bulk_add_rooms" && (
        <>
          <Cell value={draft.name} onChange={(v) => onChange({ name: v })} placeholder="Room name" />
          <Cell value={draft.abbreviation} onChange={(v) => onChange({ abbreviation: v?.toUpperCase().slice(0, 3) })} placeholder="ABR" mono />
          <Select value={draft.color_token} onChange={(v) => onChange({ color_token: v })} options={ROOM_COLOR_TOKENS} />
        </>
      )}
      {kind === "bulk_add_crew" && (
        <>
          <Cell value={draft.name} onChange={(v) => onChange({ name: v })} placeholder="Crew name" />
          <Cell value={draft.role} onChange={(v) => onChange({ role: v })} placeholder="Role" />
          <RoomSelect value={draft.primary_room_id} rooms={rooms} onChange={(v) => onChange({ primary_room_id: v })} />
        </>
      )}
      {kind === "bulk_add_equipment" && (
        <>
          <Cell value={draft.name} onChange={(v) => onChange({ name: v })} placeholder="Equipment name" />
          <Cell value={draft.equipment_type} onChange={(v) => onChange({ equipment_type: v })} placeholder="Type" />
          <NumCell value={draft.quantity_total} onChange={(v) => onChange({ quantity_total: v })} />
          <RoomSelect value={draft.primary_room_id} rooms={rooms} onChange={(v) => onChange({ primary_room_id: v })} />
        </>
      )}
      {(kind === "bulk_add_tasks" || kind === "add_task_with_subtasks") && (
        <>
          <Cell value={draft.title} onChange={(v) => onChange({ title: v })} placeholder="Task title" />
          <NumCell value={draft.duration_min} onChange={(v) => onChange({ duration_min: v })} suffix="m" />
          {kind === "bulk_add_tasks" && (
            <Select value={draft.priority} onChange={(v) => onChange({ priority: v })} options={PRIORITIES} />
          )}
          {kind === "bulk_add_tasks" && (
            <RoomSelect value={draft.primary_room_id} rooms={rooms} onChange={(v) => onChange({ primary_room_id: v })} />
          )}
        </>
      )}
      <button onClick={onRemove} className="btn-ghost text-ink-mute hover:text-red shrink-0" title="Remove row" data-testid={`bulk-row-remove-${draft._id}`}>
        <Trash2 className="w-3 h-3" />
      </button>
    </div>
  );
}

function ParentRow({ draft, rooms, onChange }) {
  return (
    <div className="grid items-center gap-2"
         style={{ gridTemplateColumns: "2fr 80px 100px 1.5fr" }}>
      <Cell value={draft.title} onChange={(v) => onChange({ title: v })} placeholder="Parent task title" />
      <NumCell value={draft.duration_min} onChange={(v) => onChange({ duration_min: v })} suffix="m" />
      <Select value={draft.priority} onChange={(v) => onChange({ priority: v })} options={PRIORITIES} />
      <RoomSelect value={draft.primary_room_id} rooms={rooms} onChange={(v) => onChange({ primary_room_id: v })} />
    </div>
  );
}

function gridFor(kind) {
  if (kind === "bulk_add_rooms") return "2fr 80px 110px 28px";
  if (kind === "bulk_add_crew") return "1.5fr 1.5fr 1fr 28px";
  if (kind === "bulk_add_equipment") return "1.5fr 1fr 70px 1fr 28px";
  if (kind === "bulk_add_tasks") return "2fr 70px 100px 1fr 28px";
  if (kind === "add_task_with_subtasks") return "1fr 70px 28px";
  return "1fr 28px";
}

function Cell({ value, onChange, placeholder, mono }) {
  return (
    <input
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={clsx(
        "bg-bg-base text-xs p-1.5 outline-none border border-transparent focus:border-amber",
        mono && "mono uppercase tracking-wider"
      )}
    />
  );
}

function NumCell({ value, onChange, suffix }) {
  return (
    <div className="flex items-center gap-1 bg-bg-base border border-transparent focus-within:border-amber">
      <input
        type="number"
        min="0"
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" ? null : parseInt(e.target.value, 10))}
        className="mono text-xs p-1.5 bg-transparent outline-none w-full tabular-nums"
      />
      {suffix && <span className="mono text-2xs text-ink-mute pr-1.5">{suffix}</span>}
    </div>
  );
}

function Select({ value, onChange, options }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="bg-bg-base text-xs p-1.5 outline-none border border-transparent focus:border-amber"
    >
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function RoomSelect({ value, rooms, onChange }) {
  return (
    <select
      value={value || ""}
      onChange={(e) => onChange(e.target.value || null)}
      className="bg-bg-base text-xs p-1.5 outline-none border border-transparent focus:border-amber truncate"
    >
      <option value="">— no room —</option>
      {rooms.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
    </select>
  );
}
